/**
 * Authentication Store — Local Auth Edition
 *
 * Manages authentication state using Zustand with local email/password auth.
 * Replaces Firebase with JWT tokens stored in localStorage.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { API_BASE, fetchApi } from '../services/apiClient';

interface LocalUser {
  user_id: number;
  email: string;
}

interface UserProfile {
  id: number;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  first_name: string | null;
  last_name: string | null;
  role: string;
  organization_name: string | null;
  preferences: Record<string, any>;
  is_active: boolean;
}

interface AuthState {
  // State
  user: LocalUser | null;
  profile: UserProfile | null;
  token: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // Actions
  initialize: () => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  fetchProfile: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  clearError: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      profile: null,
      token: null,
      isLoading: false,
      isInitialized: false,
      error: null,

      // Initialize auth - restore token from localStorage
      initialize: async () => {
        set({ isLoading: true });
        try {
          const savedToken = localStorage.getItem('auth_token');
          const userId = localStorage.getItem('user_id');
          const email = localStorage.getItem('user_email');

          // A login that stored an undefined token (the pre-fix field-name
          // bug) leaves the literal strings 'undefined'/'null' in storage.
          // Treat those as no token so the user is sent to the login page
          // instead of silently sending `Bearer undefined` on every request.
          const tokenIsValid =
            !!savedToken && savedToken !== 'undefined' && savedToken !== 'null';

          if (tokenIsValid && userId && email) {
            set({
              user: { user_id: parseInt(userId), email },
              token: savedToken,
            });
            // Try to fetch fresh profile
            await get().fetchProfile();
          }
        } catch (err) {
          console.error('Auth initialization error:', err);
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_email');
        } finally {
          set({ isLoading: false, isInitialized: true });
        }
      },

      // Email/password login
      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null });
        try {
          const response = await fetch(`${API_BASE}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            const message = errorData.message || 'Login failed. Please check your credentials.';
            set({ error: message });
            throw new Error(message);
          }

          const data = await response.json();
          const user = { user_id: data.user_id, email: data.email };

          // Store token and user info. The backend serializes the JWT under
          // the key `access_token` (LoginResponse renames token → access_token),
          // so we must read `data.access_token` here — reading `data.token`
          // yields undefined, which silently strips the Authorization header
          // from every subsequent request (manifesting as 401 "Missing
          // Authorization header" on auth-required endpoints like run-sql).
          const accessToken = data.access_token;
          localStorage.setItem('auth_token', accessToken);
          localStorage.setItem('user_id', data.user_id.toString());
          localStorage.setItem('user_email', data.email);

          set({ user, token: accessToken });
          await get().fetchProfile();
        } catch (err: any) {
          const message = err.message || 'Network error. Please try again.';
          set({ error: message });
          throw new Error(message);
        } finally {
          set({ isLoading: false });
        }
      },

      // Logout
      logout: async () => {
        set({ isLoading: true, error: null });
        try {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user_id');
          localStorage.removeItem('user_email');
          set({ user: null, profile: null, token: null });
        } catch (err: any) {
          set({ error: err.message });
          throw err;
        } finally {
          set({ isLoading: false });
        }
      },

      // Refresh token (no-op for stateless JWT, but keep for compatibility)
      refreshToken: async () => {
        const { token } = get();
        return token;
      },

      // Fetch user profile from backend
      fetchProfile: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await fetchApi(`${API_BASE}/auth/me`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            console.error('Failed to fetch profile');
            return;
          }

          const profile = await response.json();
          set({ profile });
        } catch (err) {
          console.error('Failed to fetch profile:', err);
        }
      },

      // Update user profile
      updateProfile: async (updates: Partial<UserProfile>) => {
        const { token } = get();
        if (!token) throw new Error('Not authenticated');

        try {
          const response = await fetchApi(`${API_BASE}/auth/me`, {
            method: 'PATCH',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(updates),
          });

          if (!response.ok) {
            throw new Error('Failed to update profile');
          }

          const profile = await response.json();
          set({ profile });
        } catch (err: any) {
          set({ error: err.message });
          throw err;
        }
      },

      // Clear error
      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        // JWT is in localStorage, don't persist in zustand storage
      }),
    }
  )
);

// Hook for checking if user is authenticated
export function useIsAuthenticated(): boolean {
  const { user, isInitialized } = useAuthStore();
  return isInitialized && user !== null;
}

// Hook for getting auth header
export function useAuthHeader(): string | null {
  const { token } = useAuthStore();
  return token ? `Bearer ${token}` : null;
}
